import type { Cents } from "../domain/money";
import { addMonth } from "./financing";
import {
  combinePortfolio,
  shiftPlan,
  type CapitalInputs,
  type PortfolioResult,
  type ProjectPlan,
} from "./capitalPlan";

/**
 * PROJECT-START OPTIMIZER (spec §73S/T).
 *
 * The CPM schedules WORK inside a project; this schedules PROJECT STARTS
 * across the business. Version 1 moves ONLY start dates — it never
 * reorders tasks, never changes scope, never touches a committed schedule
 * (the result is a set of PLANNING overrides the user applies explicitly).
 *
 * DETERMINISTIC BY CONSTRUCTION: a bounded candidate grid, a greedy
 * best-insertion pass in a fixed order, then improvement passes until
 * stable (capped). Same inputs, same recommendation. No model, no
 * randomness, no LLM decides a number.
 *
 * A subject that has already STARTED (any recorded actual event, or an
 * anchor in the past) is FIXED — the past cannot be rescheduled.
 */

export type PlanObjective = "balanced" | "max-profit" | "max-throughput" | "min-peak-cash";

export interface OptimizerConstraints {
  minReserveCents: Cents;
  horizonEnd: string;
  today: string;
  /** At most this many subjects under renovation at once; null = no cap. */
  maxConcurrent: number | null;
}

export interface OptimizedStart {
  subject: ProjectPlan["subject"];
  /** null = leave it where it is (fixed, or already best). */
  recommendedStart: string | null;
  currentStart: string | null;
  fixed: boolean;
  reason: string;
}

export interface OptimizerResult {
  objective: PlanObjective;
  starts: OptimizedStart[];
  /** The portfolio as it stands today. */
  baseline: PortfolioResult;
  /** The portfolio with every recommended start applied. */
  optimized: PortfolioResult;
  feasible: boolean;
  /** How the objective ranked plans, in words — no black box. */
  scoringNote: string;
  /** Candidate dates examined per movable subject (bounded, documented). */
  candidatesConsidered: number;
}

const SCORING_NOTES: Record<PlanObjective, string> = {
  balanced:
    "Balanced ranks schedules lexicographically: fewest dollars required beyond cash + LOC first, then fewest days below the reserve, then highest net position change, then earlier average start. Ties break on the earlier start date.",
  "max-profit":
    "Max profit ranks schedules by feasibility (dollars required beyond cash + LOC) first, then highest net position change within the horizon. Ties break on the earlier start date.",
  "max-throughput":
    "Max throughput ranks schedules by feasibility first, then most projects exited within the horizon, then highest net position change. Ties break on the earlier start date.",
  "min-peak-cash":
    "Min peak cash ranks schedules by lowest external cash required, then lowest peak LOC balance, then highest net position change. Ties break on the earlier start date.",
};

/** Lexicographic score — SMALLER ARRAY WINS (compared element by element). */
function score(result: PortfolioResult, objective: PlanObjective, avgStartDay: number): number[] {
  switch (objective) {
    case "balanced":
      return [
        result.peakExternalCashRequiredCents,
        result.reserveViolations.length,
        -result.netPositionChangeCents,
        avgStartDay,
      ];
    case "max-profit":
      return [result.peakExternalCashRequiredCents, -result.netPositionChangeCents, avgStartDay];
    case "max-throughput":
      return [
        result.peakExternalCashRequiredCents,
        -result.completedWithinHorizon,
        -result.netPositionChangeCents,
        avgStartDay,
      ];
    case "min-peak-cash":
      return [
        result.peakExternalCashRequiredCents,
        result.locPeakCents,
        -result.netPositionChangeCents,
        avgStartDay,
      ];
  }
}

const better = (a: number[], b: number[]): boolean => {
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const av = a[i] ?? 0;
    const bv = b[i] ?? 0;
    if (av !== bv) return av < bv;
  }
  return false;
};

const dayOf = (date: string): number => Math.round(Date.parse(`${date}T00:00:00Z`) / 86400000);

/** How many subjects are mid-renovation on any day, at these starts. */
function maxConcurrency(plans: readonly ProjectPlan[]): number {
  const intervals = plans
    .filter((p) => p.anchorDate !== null)
    .map((p) => ({ start: p.anchorDate!, end: p.exitDate ?? "9999-12-31" }));
  const boundaries = [...new Set(intervals.flatMap((i) => [i.start, i.end]))].sort();
  let peak = 0;
  for (const b of boundaries) {
    peak = Math.max(peak, intervals.filter((i) => i.start <= b && b < i.end).length);
  }
  return peak;
}

/**
 * The bounded candidate grid for movable starts: the first of each month
 * inside the horizon, plus the day after each other subject's exit (the
 * natural "start when capital frees up" points). Capped at 18 dates.
 */
export function candidateStarts(
  plans: readonly ProjectPlan[],
  constraints: OptimizerConstraints,
): string[] {
  const dates = new Set<string>();
  let month = constraints.today;
  for (let i = 0; i < 14 && month <= constraints.horizonEnd; i++) {
    dates.add(month);
    month = addMonth(month);
  }
  for (const plan of plans) {
    if (plan.exitDate && plan.exitDate > constraints.today && plan.exitDate <= constraints.horizonEnd) {
      dates.add(plan.exitDate);
    }
  }
  return [...dates].sort().slice(0, 18);
}

export function optimizeStarts(
  plans: readonly ProjectPlan[],
  capital: CapitalInputs,
  objective: PlanObjective,
  constraints: OptimizerConstraints,
  businessPlan?: ProjectPlan,
): OptimizerResult {
  const baseline = combinePortfolio(plans, capital, constraints.horizonEnd, constraints.minReserveCents,businessPlan);

  const isFixed = (plan: ProjectPlan): boolean =>
    plan.events.some((e) => e.actual) ||
    (plan.anchorDate !== null && plan.anchorDate <= constraints.today);

  // Deterministic order: biggest cash requirement first (its placement
  // shapes everything else), then by name, then id.
  const movable = plans
    .filter((p) => !isFixed(p) && p.anchorDate !== null)
    .sort(
      (a, b) =>
        b.peakCashRequiredCents - a.peakCashRequiredCents ||
        a.subject.name.localeCompare(b.subject.name) ||
        a.subject.id.localeCompare(b.subject.id),
    );
  const fixed = plans.filter((p) => isFixed(p) || p.anchorDate === null);
  const candidates = candidateStarts(plans, constraints);

  const evaluate = (arrangement: ReadonlyMap<string, ProjectPlan>): { score: number[]; result: PortfolioResult } => {
    const all = [...fixed, ...movable.map((p) => arrangement.get(p.subject.id) ?? p)];
    if (constraints.maxConcurrent !== null && maxConcurrency(all) > constraints.maxConcurrent) {
      // Infeasible arrangements rank behind every feasible one.
      return { score: [Number.MAX_SAFE_INTEGER], result: baseline };
    }
    const result = combinePortfolio(all, capital, constraints.horizonEnd, constraints.minReserveCents,businessPlan);
    const starts = all.filter((p) => p.anchorDate !== null).map((p) => dayOf(p.anchorDate!));
    const avg = starts.length > 0 ? starts.reduce((s, d) => s + d, 0) / starts.length : 0;
    return { score: score(result, objective, avg), result };
  };

  // Greedy: place each movable subject at its best candidate given the
  // others' current placement; repeat until a pass changes nothing.
  const placement = new Map<string, ProjectPlan>(movable.map((p) => [p.subject.id, p]));
  let candidatesConsidered = 0;
  for (let pass = 0; pass < 3; pass++) {
    let changed = false;
    for (const plan of movable) {
      let best = evaluate(placement);
      let bestPlan = placement.get(plan.subject.id)!;
      for (const date of candidates) {
        if (date === bestPlan.anchorDate) continue;
        // A start can be DELAYED, never pulled earlier than the date the
        // user chose — the optimizer schedules waiting, not time travel.
        if (plan.anchorDate !== null && date < plan.anchorDate) continue;
        candidatesConsidered++;
        const trial = new Map(placement);
        trial.set(plan.subject.id, shiftPlan(plan, date));
        const evaluated = evaluate(trial);
        if (better(evaluated.score, best.score)) {
          best = evaluated;
          bestPlan = trial.get(plan.subject.id)!;
        }
      }
      if (bestPlan !== placement.get(plan.subject.id)) {
        placement.set(plan.subject.id, bestPlan);
        changed = true;
      }
    }
    if (!changed) break;
  }

  const finalArrangement = [...fixed, ...movable.map((p) => placement.get(p.subject.id)!)];
  const optimized = combinePortfolio(finalArrangement, capital, constraints.horizonEnd, constraints.minReserveCents,businessPlan);

  const starts: OptimizedStart[] = plans.map((plan) => {
    if (isFixed(plan) || plan.anchorDate === null) {
      return {
        subject: plan.subject,
        recommendedStart: null,
        currentStart: plan.anchorDate,
        fixed: true,
        reason: plan.events.some((e) => e.actual)
          ? "Already under way — recorded money anchors it; the past is not rescheduled."
          : plan.anchorDate === null
            ? "No start date can be derived yet."
            : "Its start date has passed.",
      };
    }
    const placed = placement.get(plan.subject.id)!;
    const moved = placed.anchorDate !== plan.anchorDate;
    return {
      subject: plan.subject,
      recommendedStart: moved ? placed.anchorDate : null,
      currentStart: plan.anchorDate,
      fixed: false,
      reason: moved
        ? `Moving the start from ${plan.anchorDate} to ${placed.anchorDate} ranks better under the ${objective} objective (see the scoring note).`
        : "Its current start already ranks best among the candidate dates.",
    };
  });

  return {
    objective,
    starts,
    baseline,
    optimized,
    feasible: optimized.peakExternalCashRequiredCents === 0,
    scoringNote: SCORING_NOTES[objective],
    candidatesConsidered,
  };
}

/* ------------------------------------------------------------------ *
 * Financial recommendations (spec §73V/W/X/Y)
 * ------------------------------------------------------------------ */

export type RecommendationConfidence = "high-confidence" | "planning-suggestion" | "scenario";

export interface Recommendation {
  id: string;
  confidence: RecommendationConfidence;
  title: string;
  detail: string;
}

/**
 * Recommendations are DERIVED STATEMENTS about the numbers, labelled by
 * how solid their ground is — never a percentage invented for comfort.
 *
 * THE SAFETY RULE (spec §73X) is enforced by construction: nothing here
 * can recommend inflating a cost, misstating progress, or fabricating a
 * document, because every recommendation is a template over the derived
 * figures. Where lender money could tempt a shortcut, the output says to
 * check the loan terms — contractual permission is never inferred from
 * silence.
 */
export function buildRecommendations(
  result: PortfolioResult,
  plans: readonly ProjectPlan[],
  capital: CapitalInputs,
  minReserveCents: Cents,
): Recommendation[] {
  const out: Recommendation[] = [];
  const fmt = (cents: Cents) =>
    `$${(Math.abs(cents) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (result.peakExternalCashRequiredCents > 0) {
    out.push({
      id: "external-cash",
      confidence: "high-confidence",
      title: `This plan needs ${fmt(result.peakExternalCashRequiredCents)} beyond cash and the LOC`,
      detail:
        "As scheduled, projected outflows exceed every source the plan knows about. Delay a start, add capital, arrange financing for the shortfall, or reduce scope — the plan is not executable as it stands.",
    });
  }
  if (result.reserveViolations.length > 0 && result.peakExternalCashRequiredCents === 0) {
    const worst = result.reserveViolations.reduce((a, b) => (b.cashCents < a.cashCents ? b : a));
    out.push({
      id: "reserve",
      confidence: "high-confidence",
      title: `Cash falls below the ${fmt(minReserveCents)} reserve on ${result.reserveViolations.length} date${result.reserveViolations.length === 1 ? "" : "s"}`,
      detail: `The lowest point is ${fmt(worst.cashCents)} on ${worst.date}. The plan executes, but with less cushion than you have said you want.`,
    });
  }
  if (capital.loc && result.locPeakCents > 0 && capital.loc.aprPctX100 !== null) {
    out.push({
      id: "loc-carry",
      confidence: "planning-suggestion",
      title: `The LOC bridges up to ${fmt(result.locPeakCents)} and costs ${fmt(result.locInterestCents)} in interest`,
      detail:
        "Every month a project waits to exit, this carrying cost grows. If a start can move without breaking the schedule, compare the interest saved against what waiting costs.",
    });
  }

  // Per-subject: unrequested draws before big planned outflows.
  for (const plan of plans) {
    const plannedDraws = plan.events.filter((e) => e.kind === "lender-draw" && !e.actual);
    if (plannedDraws.length > 0) {
      out.push({
        id: `draws-${plan.subject.id}`,
        confidence: "planning-suggestion",
        title: `${plan.subject.name}: ${plannedDraws.length} lender draw${plannedDraws.length === 1 ? "" : "s"} not yet funded`,
        detail:
          "The plan counts on this money arriving when scheduled. Submit draw requests as the work completes, and check the loan terms before using reimbursed funds on another project — reimbursement rarely changes what the money is contractually for.",
      });
    }
  }

  // Capital allocation (spec §73W): what fits, in plain terms.
  const available =
    capital.cashCents + (capital.loc ? capital.loc.limitCents - capital.loc.balanceCents : 0);
  const waiting = plans.filter(
    (p) => p.subject.kind === "scenario" || !p.events.some((e) => e.actual),
  );
  for (const plan of waiting) {
    if (plan.peakCashRequiredCents > available) {
      out.push({
        id: `fit-${plan.subject.id}`,
        confidence: plan.subject.kind === "scenario" ? "scenario" : "planning-suggestion",
        title: `${plan.subject.name} needs ${fmt(plan.peakCashRequiredCents)} at its peak — more than the ${fmt(available)} on hand`,
        detail:
          "On its own it does not fit today's capital. It can fit later, after another project exits, or with additional financing recorded against it.",
      });
    }
  }

  if (out.length === 0) {
    out.push({
      id: "clear",
      confidence: "high-confidence",
      title: "The plan fits within cash and credit as scheduled",
      detail: "No shortfall, no reserve dip. Figures marked scenario remain possibilities, not commitments.",
    });
  }
  return out;
}
